import React, { useCallback, useMemo } from "react"
import { Routes, Route } from "react-router-dom"
import _ from "lodash"

import { useVenue } from "providers/venue"

// Theme
import { generateTheme } from "theme/generateTheme"
import { appThemeOptionFactory as directoryThemeOptionFactory } from "./theme/app/directory"

import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

import { MapOrdinal } from "pages/maps/[ordinal].kiosk"
import { Detail as PlaceDetailPage } from "pages/maps/place/[placeId]/index.kiosk"
import { Layout as MapLayout } from "pages/maps/_layout.kiosk"
import { DirectionLayout } from "pages/maps/dir/[origin]/[destination]/_layout.kiosk"

import { DirectionOverviewPage } from "pages/maps/dir/[origin]/[destination]/index.kiosk"
import { StepByStep } from "pages/maps/dir/[origin]/[destination]/navigate/steps/[step]/index.kiosk"

import { AppLayout, PageLayout, MapPageLayout } from "layouts/kiosk-h"

import { ScrollToTop } from "components/ScrollToTop"

import { homeNavigations, standardRoutes } from "standard"

import { useIdleTimer } from "react-idle-timer"
import { useNavigate } from "react-router-dom"
import { AmenityByCategory } from "./pages/maps/amenities/[category].kiosk"

import {
  getAllExtensionRoutes,
  getExtensionThemeFactories,
  mergeRoutes,
} from "./utils/extension"
import { extensionConfigs } from "./extensions"

import config from "./config"
import { LoadingScreen } from "./components/LoadingScreen"
import { AnimatePresence } from "framer-motion"
import { useAppUI } from "./providers/ui"
import { MotionDiv, showContent } from "./components/motions"
import { HomePage } from "./pages/home/index.kiosk"

const resetEvent = new Event("appreset")

// Extension routes
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs
)

// Merge Standard + Extension routes
const mergedRoutes = mergeRoutes(
  _.get(standardRoutes, config("device", "mobile")),
  extensionRoutes
)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs
)

// App Theme Factory
const appThemeOptionFactory = _.get(
  directoryThemeOptionFactory,
  config("device", "mobile")
)

export const KioskHApp = () => {
  // Generate a venue theme
  const { themeOptions: venueThemeOptions, dataLoaded } = useVenue()
  const { mapReady } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
  })
  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(() => {
    navigate("/")
    window.dispatchEvent(resetEvent)
  }, [navigate])

  useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 5,
    throttle: 500,
    disabled: config("device", "mobile") === "mobile",
  })

  return (
    <ThemeProvider theme={theme}>
      <ScrollToTop />
      <AnimatePresence mode="sync">
        {!isAppReady && <LoadingScreen />}
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route path="/" element={<AppLayout />}>
              <Route
                index
                element={<HomePage navigations={homeNavigations} />}
              />
              <Route path="/maps/*" element={<MapLayout />}>
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                <Route
                  path="ordinal/:ordinal"
                  element={<MapOrdinal Layout={MapPageLayout} />}
                />
                <Route
                  path="place/:placeId"
                  element={<PlaceDetailPage Layout={MapPageLayout} />}
                />
                <Route
                  path="amenities/:category"
                  element={<AmenityByCategory Layout={MapPageLayout} />}
                />
                <Route
                  path="dir/:origin/:destination"
                  element={<DirectionLayout />}
                >
                  <Route
                    index
                    element={<DirectionOverviewPage Layout={MapPageLayout} />}
                  />
                  <Route
                    path="navigate/steps/:step"
                    element={<StepByStep Layout={MapPageLayout} />}
                  />
                </Route>
              </Route>
              <Route path="/*" element={<PageLayout />}>
                {/* Add or override specific route extension pages here. */}
                {mergedRoutes.map(({ path, ...routeProps }) => (
                  <Route key={path} path={path} {...routeProps} />
                ))}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
    </ThemeProvider>
  )
}
